import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Eye, EyeOff, FileText, Trash2, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { DOC_TYPES, FILE_PHASES, FRONT_OFFICE } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { openFile, SecureImage } from "./SecureImage";
import { PhotoUploader } from "./PhotoUploader";

export function PhotoGallery({ meta, files: controlled, onChange, canUpload = false, allowPdf = false, uploaderExtra = {}, emptyText = "Aucune photo", size = "md", testId = "photo-gallery" }) {
  const { user } = useAuth();
  const enabled = !controlled && !!meta?.entity_id;
  const fetched = useFetch(() => api.get("/files", { params: { entity_type: meta.entity_type, entity_id: meta.entity_id, task_id: meta.task_id || undefined, phase: meta.phase || undefined } }), [meta?.entity_type, meta?.entity_id, meta?.task_id, meta?.phase], { enabled });
  const files = controlled ?? fetched.data ?? [];
  const [index, setIndex] = useState(null);
  const changed = (evt) => {
    onChange?.(evt);
    if (enabled) fetched.reload();
  };
  const tile = size === "lg" ? "h-32 w-32" : "h-24 w-24";
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-start gap-2">
        {canUpload && <PhotoUploader meta={{ ...meta, ...uploaderExtra }} allowPdf={allowPdf} onUploaded={(f) => changed({ type: "add", file: f })} className={size === "lg" ? "w-32" : "w-24"} testId={`${testId}-uploader`} />}
        {files.map((f, i) => <Tile key={f.id} file={f} className={tile} onClick={() => setIndex(i)} testId={`${testId}-item-${f.id}`} />)}
      </div>
      {!files.length && !canUpload && emptyText && <p className="text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</p>}
      {files.length > 0 && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`${testId}-count`}>{files.length} fichier{files.length > 1 ? "s" : ""}</p>}
      {index !== null && files[index] && <Lightbox files={files} index={index} onIndex={setIndex} onClose={() => setIndex(null)} user={user} onChanged={changed} testId={`${testId}-lightbox`} />}
    </div>
  );
}

function Tile({ file, className, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={cn("group relative shrink-0 overflow-hidden rounded-xl border border-border bg-muted transition-transform duration-200 hover:scale-[1.03]", className)}>
      {file.kind === "photo" ? (
        <SecureImage fileId={file.id} alt={file.caption || file.original_filename} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center"><FileText className="h-7 w-7 text-primary" /><span className="line-clamp-2 text-[10px] font-medium leading-tight">{file.original_filename}</span></div>
      )}
      {file.shared_with_client && <span className="absolute left-1 top-1 rounded-full bg-emerald-500 p-1 text-white" title="Visible par le client"><Eye className="h-3 w-3" /></span>}
      {file.doc_type && <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 text-[10px] text-white">{DOC_TYPES[file.doc_type] || file.doc_type}</span>}
    </button>
  );
}

function Lightbox({ files, index, onIndex, onClose, user, onChanged, testId }) {
  const f = files[index];
  const [caption, setCaption] = useState(f.caption || "");
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    setCaption(f.caption || "");
    setZoom(false);
  }, [f.id, f.caption]);
  const canEdit = !!f.uploaded_by && (f.uploaded_by.id === user.id || FRONT_OFFICE.includes(user.role) || (["purchase_order", "delivery_note"].includes(f.entity_type) && user.role === "parts_manager"));
  const canShare = FRONT_OFFICE.includes(user.role) && !["purchase_order", "delivery_note"].includes(f.entity_type);
  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/files/${f.id}`, body);
      onChanged({ type: "update", file: data });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("Fichier supprimé");
      onChanged({ type: "remove", file: f });
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl gap-3 p-4" aria-describedby={undefined} data-testid={testId}>
        <DialogHeader><DialogTitle className="text-base">{FILE_PHASES[f.phase] || "Fichier"}{f.doc_type ? ` · ${DOC_TYPES[f.doc_type] || f.doc_type}` : ""} <span className="text-muted-foreground">({index + 1}/{files.length})</span></DialogTitle></DialogHeader>
        <LightboxViewer file={f} caption={caption} zoom={zoom} onZoom={() => setZoom((z) => !z)} total={files.length} onPrev={() => onIndex((index - 1 + files.length) % files.length)} onNext={() => onIndex((index + 1) % files.length)} testId={testId} />
        <LightboxControls file={f} caption={caption} setCaption={setCaption} canEdit={canEdit} canShare={canShare} onPatch={patch} onRemove={remove} testId={testId} />
      </DialogContent>
    </Dialog>
  );
}

function LightboxViewer({ file: f, caption, zoom, onZoom, total, onPrev, onNext, testId }) {
  return (
    <>
      <div className="relative flex min-h-[50vh] items-center justify-center overflow-auto rounded-xl bg-black/90">
        {f.kind === "photo" ? (
          <SecureImage fileId={f.id} alt={caption} onClick={onZoom} className={cn("max-h-[70vh] object-contain transition-transform duration-300", zoom ? "scale-[2] cursor-zoom-out" : "cursor-zoom-in")} data-testid={`${testId}-image`} />
        ) : (
          <div className="flex flex-col items-center gap-3 p-8 text-white"><FileText className="h-16 w-16" /><p className="text-sm">{f.original_filename}</p><Button variant="secondary" onClick={() => openFile(f.id)} data-testid={`${testId}-open-pdf`}>Ouvrir le document</Button></div>
        )}
        {total > 1 && (
          <>
            <Button size="icon" variant="secondary" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full" onClick={onPrev} data-testid={`${testId}-prev`}><ChevronLeft className="h-5 w-5" /></Button>
            <Button size="icon" variant="secondary" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full" onClick={onNext} data-testid={`${testId}-next`}><ChevronRight className="h-5 w-5" /></Button>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span data-testid={`${testId}-meta`}>{f.uploaded_by?.name ? `${f.uploaded_by.name} · ` : ""}{fmtDateTime(f.created_at)}{f.size ? ` · ${Math.round(f.size / 1024)} Ko` : ""}</span>
        {f.kind === "photo" && <Button size="sm" variant="ghost" onClick={onZoom} data-testid={`${testId}-zoom`}>{zoom ? <ZoomOut className="mr-1 h-4 w-4" /> : <ZoomIn className="mr-1 h-4 w-4" />} {zoom ? "Réduire" : "Zoomer"}</Button>}
      </div>
    </>
  );
}

function LightboxControls({ file: f, caption, setCaption, canEdit, canShare, onPatch, onRemove, testId }) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => setConfirm(false), [f.id]);
  if (!canEdit && !canShare) return <p className="text-sm">{f.caption || <span className="text-muted-foreground">Sans légende</span>}</p>;
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-center">
      {canEdit ? (
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== (f.caption || "") && onPatch({ caption })} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} placeholder="Légende (optionnelle)" data-testid={`${testId}-caption`} />
      ) : (
        <p className="text-sm">{f.caption || <span className="text-muted-foreground">Sans légende</span>}</p>
      )}
      {canShare && <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.shared_with_client} onCheckedChange={(v) => onPatch({ shared_with_client: v })} data-testid={`${testId}-share`} />{f.shared_with_client ? <Eye className="h-4 w-4 text-emerald-500" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />} Visible par le client</label>}
      {canEdit && (confirm ? <Button variant="destructive" size="sm" onClick={onRemove} data-testid={`${testId}-delete-confirm`}>Confirmer la suppression</Button> : <Button variant="outline" size="sm" onClick={() => setConfirm(true)} data-testid={`${testId}-delete`}><Trash2 className="mr-1 h-4 w-4" /> Supprimer</Button>)}
    </div>
  );
}
